import {
  computePosition,
  shift,
  offset,
  autoUpdate,
  flip,
} from "./floating-ui/floating-ui.dom.1cfff37897af.js";

/**
 * Maps data-position enum used by Pit Viper to Floating UI's synonyms.
 */
const POSITION_MAP = {
  "top-left": "top-start",
  "top-center": "top",
  "top-right": "top-end",
  "bottom-left": "bottom-start",
  "bottom-center": "bottom",
  "bottom-right": "bottom-end",
  "center-right": "right",
  "center-left": "left",
  "right-center": "right",
  "left-center": "left",
};

const DEFAULT_PLACEMENT = "bottom-start";
const OFFSET_DISTANCE = 8;
const BOUNDARY_PADDING = 8;

/**
 * A <tqc-tooltip> wraps floating-ui to manage several positioning edge cases,
 * and has custom touch and mouse event handling.
 *
 * - With a mouse, hovering the trigger reveals the tooltip content
 * - With a touch device, tapping the trigger reveals the tooltip content,
 *    which can be closed by either tapping the trigger again,
 *    or tapping away from the content.
 */
class TqcTooltip extends HTMLElement {
  constructor() {
    super();
    this.cleanup = null;
    this.isVisible = false;
    this.originalParent = null;
    this.openedViaTouch = false;
    this.handleOutsideTouch = this.handleOutsideTouch.bind(this);
    this.handleTooltipContentTouch = this.handleTooltipContentTouch.bind(this);
  }

  connectedCallback() {
    this.tooltipElement = this.querySelector('[role="tooltip"]');
    if (!this.tooltipElement) {
      console.log("tqc-tooltip missing role='tooltip' child");
      return;
    }
    const dataPosition = this.getAttribute("data-position") || "";
    this.placement = POSITION_MAP[dataPosition] || DEFAULT_PLACEMENT;
    this.addEventListener("mouseenter", this.show);
    this.addEventListener("mouseleave", this.hide);
    this.addEventListener("focus", this.show, true);
    this.addEventListener("blur", this.hide, true);
    this.addEventListener("touchstart", this.handleTouch);
    this.addEventListener("mousedown", this.handleMouseDown);
    this.addEventListener("click", this.handleClick);
  }

  disconnectedCallback() {
    this.removeEventListener("mouseenter", this.show);
    this.removeEventListener("mouseleave", this.hide);
    this.removeEventListener("focus", this.show, true);
    this.removeEventListener("blur", this.hide, true);
    this.removeEventListener("touchstart", this.handleTouch);
    this.removeEventListener("mousedown", this.handleMouseDown);
    this.removeEventListener("click", this.handleClick);
    this.removeOutsideTouchListener();

    if (this.tooltipElement) {
      this.hide();
    }
  }

  /**
   * `touchstart` on the tooltip trigger should toggle it open or closed.
   */
  handleTouch = (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (this.isVisible) {
      this.hide();
    } else {
      this.openedViaTouch = true;
      this.show();
    }
  };

  /**
   * Mouse interactions (click or press) inside TomSelect dropdowns should not
   * bubble up, otherwise TomSelect will treat them as option selection.
   */
  handleMouseDown = (event) => {
    // Left clicks only
    if (event.button !== 0) return;
    if (!this.shouldIgnoreMouseEvent(event)) return;
    event.preventDefault();
    event.stopPropagation();
  };

  handleClick = (event) => {
    if (!this.shouldIgnoreMouseEvent(event)) return;
    event.preventDefault();
    event.stopPropagation();
  };

  /**
   * `touchstart` on the tooltip content should do nothing - the content should stay open.
   *
   * This is a special case because the content appended to the end of the <body>
   * to fix several positioning edge cases, so it's technically "outside", but we
   * do not want to engage the typical "touched outside" behavior.
   */
  handleTooltipContentTouch(event) {
    // Check if the touch target is the tooltip content itself or within it
    const isTouchingTooltip =
      event.target === this.tooltipElement ||
      this.tooltipElement.contains(event.target);

    if (isTouchingTooltip) {
      // Prevent touches on tooltip content from propagating to underlying elements
      // This is critical for tooltips inside dropdowns (like TomSelect) - without this,
      // the dropdown's click-outside handler will fire and close the dropdown
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
    }
  }

  /**
   * `touchstart` anywhere outside the trigger & content should close the tooltip.   *
   */
  handleOutsideTouch(event) {
    const touchedElement = event.target;
    const isOutsideTrigger = !this.contains(touchedElement);
    const isOutsideTooltip =
      touchedElement !== this.tooltipElement &&
      !this.tooltipElement.contains(touchedElement);

    // Check if the touch is on another tooltip trigger
    const isTouchingAnotherTooltip =
      touchedElement.closest("tqc-tooltip") !== null;

    if (isOutsideTrigger && isOutsideTooltip) {
      this.hide();

      // Don't stop propagation if we're tapping another tooltip trigger
      // (let that tooltip's handleTouch run so it can set openedViaTouch correctly)
      if (!isTouchingAnotherTooltip) {
        event.stopPropagation();
      }
    }
  }

  addOutsideTouchListener() {
    document.addEventListener("touchstart", this.handleOutsideTouch, true);
  }

  removeOutsideTouchListener() {
    document.removeEventListener("touchstart", this.handleOutsideTouch, true);
  }

  addTooltipContentTouchListener() {
    // we need { passive: false } to be able to preventDefault the touch events before
    // they reach other handlers (like TomSelect's click-outside detector). This allows
    // us to keep the tooltip open when you tap on its contents directly.
    //
    // context: touch listeners are normally considered `passive` (in other words, uninterruptible)
    // by Chrome as a performance enhancement, to prevent janky scrolling. These touch handlers
    // are simple so should not noticeably impact performance.
    document.addEventListener("touchstart", this.handleTooltipContentTouch, {
      capture: true,
      passive: false,
    });
  }

  removeTooltipContentTouchListener() {
    document.removeEventListener("touchstart", this.handleTooltipContentTouch, {
      capture: true,
      passive: false,
    });
  }

  shouldIgnoreMouseEvent(event) {
    if (!(event instanceof MouseEvent)) return false;
    if (!this.contains(event.target)) return false;

    // Keyboard-triggered clicks have detail === 0; allow those through for accessibility.
    if (event.type === "click" && event.detail === 0) {
      return false;
    }

    return this.closest(".ts-dropdown") !== null;
  }

  updatePosition = () => {
    return computePosition(this, this.tooltipElement, {
      strategy: "fixed",
      placement: this.placement,
      middleware: [
        offset(OFFSET_DISTANCE),
        flip(),
        shift({ padding: BOUNDARY_PADDING }),
      ],
    })
      .then(({ x, y }) => {
        Object.assign(this.tooltipElement.style, {
          left: `${x}px`,
          top: `${y}px`,
          bottom: "auto",
          position: "fixed",
        });
      })
      .catch((error) => {
        console.error("Tooltip positioning failed:", error);
      });
  };

  show = () => {
    // bail early if it's already showing. this is needed in edge cases where the user
    // hides the tooltip very quickly (faster than the updatePosition async call),
    // to prevent the tooltip from being stuck open
    if (this.isVisible) return;

    this.isVisible = true;

    const closestDialog = this.closest("dialog");
    const portalTarget = closestDialog || document.body;

    if (this.tooltipElement.parentElement !== portalTarget) {
      this.originalParent = this.tooltipElement.parentElement;
      portalTarget.appendChild(this.tooltipElement);
    }

    this.tooltipElement.classList.add("tqc-tooltip-visible");

    this.updatePosition().then(() => {
      if (!this.isVisible) return;
      this.tooltipElement.style.display = "block";
      this.cleanup = autoUpdate(this, this.tooltipElement, this.updatePosition);
    });

    this.addOutsideTouchListener();

    // CSS has pointer-events: none to fix mouse hover edge cases,
    // but we need to reset it to prevent the tooltip content closing
    // when you tap it.
    if (this.openedViaTouch) {
      this.tooltipElement.style.pointerEvents = "auto";
      this.addTooltipContentTouchListener();
    }
  };

  hide = () => {
    this.isVisible = false;
    this.openedViaTouch = false;

    if (this.cleanup) {
      this.cleanup();
      this.cleanup = null;
    }

    this.tooltipElement.style.display = "";
    this.tooltipElement.style.pointerEvents = "";
    this.tooltipElement.classList.remove("tqc-tooltip-visible");

    if (this.originalParent && this.originalParent.isConnected) {
      this.originalParent.appendChild(this.tooltipElement);
    }

    this.removeOutsideTouchListener();
    this.removeTooltipContentTouchListener();
  };
}

window.customElements.define("tqc-tooltip", TqcTooltip);
